import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  ClipboardList, TrendingUp, TrendingDown, Wallet,
  Sparkles, ChevronRight, ChevronDown, Tag, StickyNote, X, Play, Pencil,
  Calendar, AlertTriangle, Target, ChevronUp
} from 'lucide-react';
import InlineError from '../components/InlineError';
import OrderModal from '../components/OrderModal';
import TradeReplay from '../components/charts/TradeReplay';
import { useTheme } from '../context/ThemeContext';
import { useSocket } from '../context/SocketContext';
import { formatPrice, formatDateTime, formatDate } from '../utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function OrdersPage() {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const { onOrderFilled, offOrderFilled, onOrderCancelled, offOrderCancelled, onPositionExited, offPositionExited, onReconnect } = useSocket();
  const [activeTab, setActiveTab] = useState('today');

  // Today's orders state
  const [todayOrders, setTodayOrders] = useState([]);
  const [dailyPnl, setDailyPnl] = useState(0);
  const [dailyCharges, setDailyCharges] = useState(0);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState(null);
  const [portfolioError, setPortfolioError] = useState(null);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [cancelConfirm, setCancelConfirm] = useState(null);
  const [replayOrder, setReplayOrder] = useState(null);
  const [modifyOrder, setModifyOrder] = useState(null);   // order being modified in full modal
  const [sellPosition, setSellPosition] = useState(null); // position being sold from portfolio

  // Portfolio state
  const [portfolio, setPortfolio] = useState([]);
  const [openTrades, setOpenTrades] = useState([]);  // individual open trades for expanded detail
  const [portfolioSummary, setPortfolioSummary] = useState({ totalInvested: 0, currentValue: 0, unrealizedPnl: 0 });
  const [portfolioLoading, setPortfolioLoading] = useState(false);
  const [portfolioInsights, setPortfolioInsights] = useState({ positions: {}, summary: '' });
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [expandedSymbol, setExpandedSymbol] = useState(null);

  const formatCurrency = (v) => formatPrice(v, 0);

  // In Learn Mode use sim effective date; in Trade Mode use real today
  const getRealTodayIST = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  const [today, setToday] = useState(getRealTodayIST);

  // ── Resolve effective date — always sim_date (orders only exist in Learn Mode) ──
  useEffect(() => {
    const resolveDate = async () => {
      try {
        const res = await axios.get(`${API_URL}/market/effective-date`);
        const d = res.data?.date || getRealTodayIST();
        setToday(d);
        fetchTodayOrders(d);
      } catch {
        const d = getRealTodayIST();
        setToday(d);
        fetchTodayOrders(d);
      }
    };
    resolveDate();
  }, []); // eslint-disable-line

  const fetchTodayOrders = async (date = today) => {
    setOrdersLoading(true);
    setOrdersError(null);
    try {
      const [ordersRes, historyRes] = await Promise.allSettled([
        axios.get(`${API_URL}/trade/orders`),
        axios.get(`${API_URL}/trade/history`),
      ]);

      const allOrders = (ordersRes.status === 'fulfilled' ? ordersRes.value.data : []) || [];
      const history = (historyRes.status === 'fulfilled' ? historyRes.value.data : []) || [];

      // Active (unfilled) orders — scoped to today's sim_date (Learn Mode only; Trade Mode has no live orders)
      const activeOrders = allOrders.filter(o => {
        if (!['OPEN', 'QUEUED', 'PENDING', 'PROCESSING'].includes(o.status?.toUpperCase())) return false;
        // Match sim_date exactly, or fall back to placed_at date for legacy rows with null sim_date
        return o.sim_date === date || (!o.sim_date && o.placed_at?.slice(0, 10) === date);
      });
      const normalizedActive = activeOrders.map(o => ({
        ...o,
        _displayStatus: o.status?.toUpperCase() === 'QUEUED' ? 'QUEUED' : 'OPEN',
        trade_id: o.order_id || o.id,
        trade_type: o.order_type,
        product_type: o.product_type || (o.order_variant === 'INTRADAY' ? 'INTRADAY' : null),
        price: o.limit_price || null,
        executed_at: o.placed_at || o.queued_at,
        pnl: null,
      }));

      // Executed trades for today's sim_date — OPEN (holding) or CLOSED (exited)
      const todayTrades = history.filter(t => t.sim_date === date).map(t => ({
        ...t,
        stop_loss_price: t.stop_loss,
        _displayStatus: t.status === 'OPEN' ? 'COMPLETE' : t.status,
      }));

      // Merge: pending/queued first, then today's executed trades
      const merged = [...normalizedActive, ...todayTrades];
      setTodayOrders(merged);
      const closed = todayTrades.filter(t => t.status === 'CLOSED');
      const pnl = closed.reduce((s, t) => s + (t.pnl || 0), 0);
      const charges = closed.reduce((s, t) => s + (t.total_charges || 0), 0);
      setDailyPnl(pnl);
      setDailyCharges(charges);
    } catch (e) {
      console.error('Failed to fetch orders', e);
      setOrdersError("Couldn't load your orders.");
    } finally {
      setOrdersLoading(false);
    }
  };


  // ── Portfolio ───────────────────────────────────────────────────
  const fetchPortfolio = useCallback(async () => {
    setPortfolioLoading(true);
    setPortfolioError(null);
    try {
      const [portRes, histRes] = await Promise.all([
        axios.get(`${API_URL}/trade/portfolio`),
        axios.get(`${API_URL}/trade/history`),
      ]);
      const positions = portRes.data || [];
      setPortfolio(positions);
      const totalInvested = positions.reduce((s, p) => s + (p.total_invested || 0), 0);
      const currentValue = positions.reduce((s, p) => s + (p.current_value || 0), 0);
      setPortfolioSummary({ totalInvested, currentValue, unrealizedPnl: currentValue - totalInvested });
      // Individual open trades — for expanded detail (entry date, SL, target per lot)
      const open = (histRes.data || []).filter(t => t.status === 'OPEN' || t.status === 'open');
      setOpenTrades(open);
    } catch (e) {
      console.error('Failed to fetch portfolio', e);
      setPortfolioError("Couldn't load your portfolio.");
    } finally {
      setPortfolioLoading(false);
    }
  }, []);

  const fetchPortfolioInsights = useCallback(async () => {
    if (portfolioInsights.summary || insightsLoading) return;
    setInsightsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/ai/portfolio-insights`);
      setPortfolioInsights(res.data || { positions: {}, summary: '' });
    } catch (e) {
      // insights are non-critical — don't block the portfolio view
    } finally {
      setInsightsLoading(false);
    }
  }, [portfolioInsights.summary, insightsLoading]);

  useEffect(() => {
    if (activeTab === 'portfolio') {
      fetchPortfolio();
      fetchPortfolioInsights();
    }
  }, [activeTab]); // eslint-disable-line

  // ── Socket.io: in-place updates + reconnect re-fetch ────────────
  useEffect(() => {
    // order_filled: flip the matching OPEN row to EXECUTED in-place; refresh portfolio
    const handleOrderFilled = (data) => {
      setTodayOrders(prev => prev.map(o =>
        o.order_id === data.order_id || o.id === data.order_id
          ? { ...o, ...data, status: 'EXECUTED', _displayStatus: 'EXECUTED' }
          : o
      ));
      fetchPortfolio();
    };

    // order_cancelled: remove the matching OPEN/QUEUED row from the list
    const handleOrderCancelled = (data) => {
      setTodayOrders(prev => prev.filter(o =>
        !(o.order_id === data.order_id || o.id === data.order_id)
      ));
    };

    // position_exited: flip the matching COMPLETE row to CLOSED in-place; refresh portfolio
    const handlePositionExited = (data) => {
      setTodayOrders(prev => prev.map(o =>
        o.id === data.trade_id
          ? { ...o, status: 'CLOSED', _displayStatus: 'CLOSED', pnl: data.pnl, pnl_pct: data.pnl_pct, exit_price: data.exit_price, exit_reason: data.exit_reason }
          : o
      ));
      setDailyPnl(prev => prev + (data.pnl ?? 0));
      fetchPortfolio();
    };

    // reconnect: re-fetch to catch any events missed while disconnected
    const handleReconnect = () => fetchTodayOrders(today);

    onOrderFilled(handleOrderFilled);
    onOrderCancelled(handleOrderCancelled);
    onPositionExited(handlePositionExited);
    onReconnect(handleReconnect);
    return () => {
      offOrderFilled(handleOrderFilled);
      offOrderCancelled(handleOrderCancelled);
      offPositionExited(handlePositionExited);
    };
  }, [today]); // eslint-disable-line

  // ── Helpers ─────────────────────────────────────────────────────
  const pnlColor = (v) => (v >= 0 ? colors.profit : colors.loss);

  const statusBadge = (status) => {
    const map = {
      EXECUTED: { bg: '#16a34a22', text: '#16a34a', label: 'Executed' },
      COMPLETE: { bg: '#16a34a22', text: '#16a34a', label: 'Complete' },
      HOLDING:  { bg: '#0891b222', text: '#0891b2', label: 'Holding' },
      OPEN:     { bg: '#6366F122', text: '#6366F1', label: 'Open' },
      PENDING:  { bg: '#d9770622', text: '#d97706', label: 'Pending' },
      QUEUED:   { bg: '#7c3aed22', text: '#7c3aed', label: 'Queued' },
      CANCELLED:{ bg: '#64748b22', text: '#64748b', label: 'Cancelled' },
      CLOSED:   { bg: '#64748b22', text: '#64748b', label: 'Closed' },
    };
    const s = map[status?.toUpperCase()] || { bg: '#64748b22', text: '#64748b', label: status || 'Unknown' };
    return (
      <span className="px-1 py-0.5 rounded text-[7px] sm:text-[10px] font-semibold uppercase tracking-wide" style={{ backgroundColor: s.bg, color: s.text }}>
        {s.label}
      </span>
    );
  };

  const exitReasonBadge = (reason) => {
    if (!reason) return null;
    const map = {
      SL:     { bg: '#ef444420', text: '#ef4444', label: '⛔ SL Hit' },
      TARGET: { bg: '#16a34a20', text: '#16a34a', label: '🎯 Target Hit' },
      EOD:    { bg: '#d9770620', text: '#d97706', label: '⏰ EOD Exit' },
      MANUAL: { bg: '#64748b20', text: '#64748b', label: 'Manual' },
    };
    const s = map[reason?.toUpperCase()] || { bg: '#64748b20', text: '#64748b', label: reason };
    return (
      <span className="px-1.5 py-0.5 rounded text-[13px] font-semibold" style={{ backgroundColor: s.bg, color: s.text }}>
        {s.label}
      </span>
    );
  };

  const productTypeBadge = (pt) => {
    if (!pt) return null;
    const intra = pt?.toUpperCase() === 'INTRADAY';
    return (
      <span className="px-1.5 py-0.5 rounded text-[12px] font-semibold uppercase tracking-wide"
        style={{ backgroundColor: intra ? '#7c3aed20' : colors.inputBg, color: intra ? '#a78bfa' : colors.secondaryText }}>
        {intra ? 'Intraday' : 'Swing'}
      </span>
    );
  };

  // ── Render ───────────────────────────────────────────────────────

  return (
    <div className="pb-4">
      {/* Header */}
      <div className="mb-2">
        <h1 className="text-[17px] font-bold" style={{ color: colors.primaryText }}>Orders</h1>
        <p className="text-[13px] mt-0.5" style={{ color: colors.secondaryText }}>Today's activity & your portfolio</p>
      </div>

      {/* Tabs */}
      <div className="flex rounded-lg p-0.5 mb-4" style={{ backgroundColor: colors.inputBg }}>
        {[
          { id: 'today', label: "Today's Orders", shortLabel: "Orders" },
          { id: 'portfolio', label: 'Portfolio', shortLabel: 'Portfolio' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className="flex-1 py-1.5 rounded-md text-[13px] font-semibold transition-all"
            style={{
              backgroundColor: activeTab === tab.id ? colors.cardBg : 'transparent',
              color: activeTab === tab.id ? colors.accent : colors.secondaryText,
              boxShadow: activeTab === tab.id ? '0 1px 3px rgba(0,0,0,0.2)' : 'none',
            }}
          >
            <span className="hidden sm:inline">{tab.label}</span>
            <span className="sm:hidden">{tab.shortLabel}</span>
          </button>
        ))}
      </div>

      {/* ── TODAY'S ORDERS TAB ────────────────────────────────── */}
      {activeTab === 'today' && (
        <div>
          {/* Daily P&L summary */}
          <div className="rounded-2xl p-3 mb-2" style={{ background: `linear-gradient(135deg, ${colors.accent}14 0%, ${colors.accent}04 100%)`, border: `1px solid ${colors.accent}30` }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[12px] uppercase tracking-wide mb-0.5" style={{ color: colors.secondaryText }}>Today's P&L</p>
                <p className="text-[14px] font-bold leading-tight" style={{ color: pnlColor(dailyPnl) }}>
                  {dailyPnl >= 0 ? '+' : ''}{formatCurrency(dailyPnl)}
                </p>
                {dailyCharges > 0 && (
                  <p className="text-[11px] mt-0.5" style={{ color: colors.secondaryText }}>
                    charges: <span style={{ color: '#ef4444' }}>-{formatCurrency(dailyCharges)}</span>
                  </p>
                )}
              </div>
              <div className="text-right">
                <p className="text-[12px]" style={{ color: colors.secondaryText }}>Trades today</p>
                <p className="text-[15px] font-bold leading-tight" style={{ color: colors.primaryText }}>{todayOrders.length}</p>
              </div>
            </div>
          </div>

          {ordersLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 rounded-lg animate-pulse" style={{ backgroundColor: colors.inputBg }} />
              ))}
            </div>
          ) : ordersError ? (
            <InlineError message={ordersError} onRetry={() => fetchTodayOrders(today)} />
          ) : todayOrders.length === 0 ? (
            <div className="text-center py-8">
              <ClipboardList className="w-10 h-10 mx-auto mb-3 opacity-30" style={{ color: colors.secondaryText }} />
              <p className="text-[14px] font-medium" style={{ color: colors.secondaryText }}>No orders today</p>
              <p className="text-[13px] mt-1" style={{ color: colors.secondaryText }}>Place a trade to see it here</p>
            </div>
          ) : (
            <div className="space-y-2">
              {todayOrders.map((order, i) => {
                const symbol = (order.symbol || '').replace('.NS', '');
                const isBuy = (order.trade_type || order.type || '').toUpperCase() === 'BUY';
                const hasPnl = order.pnl != null;
                const isExpanded = expandedOrder === (order.trade_id || i);
                const tags = order.strategy_tags || order.notes_tags || [];
                const hasDetails = order.stop_loss_price || order.target_price || order.notes || tags.length > 0 || order.order_variant;
                return (
                  <div
                    key={order.trade_id || i}
                    className="rounded-2xl overflow-hidden"
                    style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, border: `1px solid ${isExpanded ? colors.accent + '60' : colors.accent + '25'}` }}
                  >
                    {/* Row header */}
                    <div
                      className="px-2.5 py-2 flex items-center gap-2.5 cursor-pointer"
                      onClick={() => setExpandedOrder(isExpanded ? null : (order.trade_id || i))}
                    >
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-[13px] font-bold shrink-0"
                        style={{ backgroundColor: isBuy ? '#16a34a22' : '#dc262622', color: isBuy ? '#16a34a' : '#dc2626' }}
                      >
                        {isBuy ? 'B' : 'S'}
                      </div>
                      <div className="flex-1 flex items-center justify-between min-w-0 gap-2">
                        {/* Left: name + qty */}
                        <div className="min-w-0">
                          <p className="text-[13px] font-semibold leading-tight truncate" style={{ color: colors.primaryText }}>{symbol}</p>
                          <p className="text-[12px]" style={{ color: colors.secondaryText }}>
                            {order.quantity} × {formatPrice(order.price || order.entry_price || 0)}
                            {order.order_variant && order.order_variant !== 'MARKET' && (
                              <span className="ml-1 opacity-60">· {order.order_variant}</span>
                            )}
                          </p>
                        </div>
                        {/* Right: P&L top, status badge bottom — self-stretch so badge always pins to bottom */}
                        <div className="self-stretch flex flex-col items-end justify-between shrink-0">
                          {hasPnl ? (
                            <span className="text-[14px] font-bold" style={{ color: pnlColor(order.pnl) }}>
                              {order.pnl >= 0 ? '+' : ''}{formatCurrency(order.pnl)}
                            </span>
                          ) : (order.quantity && (order.price || order.entry_price)) ? (
                            <span className="text-[12px]" style={{ color: colors.secondaryText }}>
                              {formatCurrency(order.quantity * (order.price || order.entry_price))}
                            </span>
                          ) : null}
                          {statusBadge(order._displayStatus || order.status)}
                        </div>
                      </div>
                      {isExpanded
                        ? <ChevronDown className="w-3 h-3 shrink-0 opacity-40" style={{ color: colors.secondaryText }} />
                        : <ChevronRight className="w-3 h-3 shrink-0 opacity-40" style={{ color: colors.secondaryText }} />
                      }
                    </div>

                    {/* Expanded detail panel */}
                    {isExpanded && (
                      <div className="px-3 pb-3 border-t" style={{ borderColor: colors.border }}>
                        <div className="pt-3 space-y-3">

                          {/* Status + type row */}
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[12px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide"
                              style={{ backgroundColor: colors.inputBg, color: colors.secondaryText }}>
                              {order.order_variant || 'MARKET'}
                            </span>
                            {productTypeBadge(order.product_type)}
                            {exitReasonBadge(order.exit_reason)}
                            {(() => {
                              const src = order.source || 'MANUAL';
                              const cfg = src === 'NYX_AI'
                                ? { bg: '#6366f120', color: '#818cf8', label: '✦ Nyx AI' }
                                : src === 'ALGO_FLOW'
                                ? { bg: '#f59e0b20', color: '#f59e0b', label: '⚙ Algo Flow' }
                                : { bg: colors.inputBg, color: colors.secondaryText, label: 'Manual' };
                              return (
                                <span className="text-[12px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide"
                                  style={{ backgroundColor: cfg.bg, color: cfg.color }}>
                                  {cfg.label}
                                </span>
                              );
                            })()}
                            <span className="text-[12px]" style={{ color: colors.secondaryText }}>
                              Placed <span className="font-semibold" style={{ color: colors.accent }}>{formatDateTime(order.executed_at || order.placed_at)}</span>
                            </span>
                            {order.sim_date && (
                              <span className="flex items-center gap-1 text-[12px] px-2 py-0.5 rounded-full font-semibold"
                                style={{ backgroundColor: '#0891b215', color: '#0891b2' }}>
                                <Calendar className="w-3 h-3" />
                                Sim {(() => { const [y,m,d] = order.sim_date.split('-'); return `${d} ${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][+m-1]} ${y}`; })()}
                              </span>
                            )}
                          </div>

                          {/* Price grid — always shown */}
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-2">
                            <div>
                              <p className="text-[12px] uppercase tracking-wide mb-0.5" style={{ color: colors.secondaryText }}>
                                {order.executed_price ? 'Exec Price' : order.limit_price ? 'Limit' : 'Price'}
                              </p>
                              <p className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>
                                {order.executed_price
                                  ? formatPrice(order.executed_price)
                                  : order.limit_price
                                  ? formatPrice(order.limit_price)
                                  : <span style={{ color: colors.secondaryText }}>Market</span>}
                              </p>
                            </div>
                            <div>
                              <p className="text-[12px] uppercase tracking-wide mb-0.5" style={{ color: colors.secondaryText }}>Stop Loss</p>
                              <p className="text-[13px] font-semibold" style={{ color: order.stop_loss_price ? '#ef4444' : colors.secondaryText }}>
                                {order.stop_loss_price ? formatPrice(order.stop_loss_price) : '—'}
                              </p>
                            </div>
                            <div>
                              <p className="text-[12px] uppercase tracking-wide mb-0.5" style={{ color: colors.secondaryText }}>Target</p>
                              <p className="text-[13px] font-semibold" style={{ color: order.target_price ? '#16a34a' : colors.secondaryText }}>
                                {order.target_price ? formatPrice(order.target_price) : '—'}
                              </p>
                            </div>
                            <div>
                              <p className="text-[12px] uppercase tracking-wide mb-0.5" style={{ color: colors.secondaryText }}>Qty</p>
                              <p className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>{order.quantity}</p>
                            </div>
                            <div>
                              <p className="text-[12px] uppercase tracking-wide mb-0.5" style={{ color: colors.secondaryText }}>Status</p>
                              <p className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>{order._displayStatus || order.status}</p>
                            </div>
                            {order.scheduled_execution_date && (
                              <div>
                                <p className="text-[12px] uppercase tracking-wide mb-0.5" style={{ color: colors.secondaryText }}>Exec Date</p>
                                <p className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>
                                  {formatDate(order.scheduled_execution_date)}
                                </p>
                              </div>
                            )}
                          </div>

                          {/* Charges breakdown — only for CLOSED trades with charges data */}
                          {order.status === 'CLOSED' && order.charges && (() => {
                            const c = typeof order.charges === 'string' ? JSON.parse(order.charges) : order.charges;
                            const grossPnl = (order.pnl || 0) + (order.total_charges || 0);
                            return (
                              <div className="rounded-lg p-2.5 space-y-1.5" style={{ backgroundColor: colors.inputBg }}>
                                <p className="text-[11px] uppercase tracking-wide font-semibold mb-1.5" style={{ color: colors.secondaryText }}>P&L Breakdown</p>
                                <div className="flex justify-between text-[12px]">
                                  <span style={{ color: colors.secondaryText }}>Gross P&L</span>
                                  <span className="font-semibold" style={{ color: pnlColor(grossPnl) }}>{grossPnl >= 0 ? '+' : ''}{formatCurrency(grossPnl)}</span>
                                </div>
                                {c.brokerage > 0 && <div className="flex justify-between text-[12px]"><span style={{ color: colors.secondaryText }}>Brokerage</span><span style={{ color: '#ef4444' }}>-{formatCurrency(c.brokerage)}</span></div>}
                                {c.stt > 0      && <div className="flex justify-between text-[12px]"><span style={{ color: colors.secondaryText }}>STT</span><span style={{ color: '#ef4444' }}>-{formatCurrency(c.stt)}</span></div>}
                                {c.exchange_txn > 0 && <div className="flex justify-between text-[12px]"><span style={{ color: colors.secondaryText }}>Exchange Txn</span><span style={{ color: '#ef4444' }}>-{formatCurrency(c.exchange_txn)}</span></div>}
                                {c.stamp_duty > 0 && <div className="flex justify-between text-[12px]"><span style={{ color: colors.secondaryText }}>Stamp Duty</span><span style={{ color: '#ef4444' }}>-{formatCurrency(c.stamp_duty)}</span></div>}
                                {c.gst > 0      && <div className="flex justify-between text-[12px]"><span style={{ color: colors.secondaryText }}>GST (18%)</span><span style={{ color: '#ef4444' }}>-{formatCurrency(c.gst)}</span></div>}
                                {c.sebi > 0     && <div className="flex justify-between text-[12px]"><span style={{ color: colors.secondaryText }}>SEBI</span><span style={{ color: '#ef4444' }}>-{formatCurrency(c.sebi)}</span></div>}
                                <div className="border-t pt-1.5 flex justify-between text-[13px] font-bold" style={{ borderColor: colors.border }}>
                                  <span style={{ color: colors.primaryText }}>Net P&L</span>
                                  <span style={{ color: pnlColor(order.pnl) }}>{(order.pnl || 0) >= 0 ? '+' : ''}{formatCurrency(order.pnl || 0)}</span>
                                </div>
                              </div>
                            );
                          })()}

                          {/* Strategy tags — with remove button */}
                          {tags.length > 0 && (
                            <div className="flex items-start gap-1.5">
                              <Tag className="w-3 h-3 mt-0.5 shrink-0" style={{ color: colors.accent }} />
                              <div className="flex flex-wrap gap-1">
                                {tags.map((tag, ti) => (
                                  <span key={ti} className="inline-flex items-center gap-0.5 text-[12px] px-1.5 py-0.5 rounded-full font-medium"
                                    style={{ backgroundColor: colors.accent + '20', color: colors.accent }}>
                                    {tag}
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        await axios.delete(`${API_URL}/trade/orders/${order.trade_id}/tags/${encodeURIComponent(tag)}`);
                                        setTodayOrders(prev => prev.map(o =>
                                          o.trade_id === order.trade_id
                                            ? { ...o, strategy_tags: (o.strategy_tags || []).filter(t => t !== tag) }
                                            : o
                                        ));
                                      }}
                                      className="ml-0.5 opacity-60 hover:opacity-100"
                                    >
                                      <X className="w-2 h-2" />
                                    </button>
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Notes / trade reason */}
                          {order.notes ? (
                            <div className="rounded-lg p-2 flex items-start gap-1.5" style={{ backgroundColor: colors.inputBg }}>
                              <StickyNote className="w-3 h-3 mt-0.5 shrink-0" style={{ color: colors.secondaryText }} />
                              <p className="text-[13px] italic leading-relaxed" style={{ color: colors.secondaryText }}>{order.notes}</p>
                            </div>
                          ) : (
                            <p className="text-[12px] italic" style={{ color: colors.secondaryText + '80' }}>No notes added</p>
                          )}

                          {/* Actions row */}
                          <div className="flex items-center justify-between pt-1">
                            <div className="flex items-center gap-3">
                              <button
                                className="text-[13px] font-semibold"
                                style={{ color: colors.accent }}
                                onClick={(e) => { e.stopPropagation(); navigate(`/stock/${symbol}`); }}
                              >
                                View {symbol} chart →
                              </button>
                              {['EXECUTED', 'CLOSED'].includes(order.status?.toUpperCase()) && order.executed_price && (
                                <button
                                  className="flex items-center gap-1 text-[13px] font-semibold"
                                  style={{ color: colors.accent }}
                                  onClick={(e) => { e.stopPropagation(); setReplayOrder(order); }}
                                >
                                  <Play className="w-2.5 h-2.5" />
                                  Replay
                                </button>
                              )}
                            </div>

                            {['OPEN', 'QUEUED'].includes(order._displayStatus?.toUpperCase()) && (
                              <div className="flex items-center gap-1.5">
                                {order.order_variant === 'LIMIT' && (
                                  <button
                                    className="flex items-center gap-1 text-[13px] font-semibold px-2 py-1 rounded-lg border"
                                    style={{ color: colors.accent, borderColor: colors.accent + '40', backgroundColor: colors.accent + '10' }}
                                    onClick={(e) => { e.stopPropagation(); setModifyOrder(order); }}
                                  >
                                    <Pencil className="w-2.5 h-2.5" />
                                    Modify
                                  </button>
                                )}
                                <button
                                  className="flex items-center gap-1 text-[13px] font-semibold px-2.5 py-1 rounded-lg border"
                                  style={{ color: '#ef4444', borderColor: '#ef444440', backgroundColor: '#ef444410' }}
                                  onClick={(e) => { e.stopPropagation(); setCancelConfirm(order); }}
                                >
                                  <X className="w-2.5 h-2.5" />
                                  Cancel
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── PORTFOLIO TAB ─────────────────────────────────────── */}
      {activeTab === 'portfolio' && (
        <div>
          {/* Portfolio summary */}
          <div className="rounded-2xl px-3 py-2 mb-2" style={{ background: `linear-gradient(135deg, ${colors.accent}14 0%, ${colors.accent}04 100%)`, border: `1px solid ${colors.accent}30` }}>
            <div className="flex items-center justify-between">
              <div className="text-center">
                <p className="text-[10px] uppercase tracking-wide" style={{ color: colors.secondaryText }}>Invested</p>
                <p className="text-[12px] font-bold" style={{ color: colors.primaryText }}>{formatCurrency(portfolioSummary.totalInvested)}</p>
              </div>
              <div className="w-px h-6" style={{ backgroundColor: colors.border }} />
              <div className="text-center">
                <p className="text-[10px] uppercase tracking-wide" style={{ color: colors.secondaryText }}>Current</p>
                <p className="text-[12px] font-bold" style={{ color: colors.primaryText }}>{formatCurrency(portfolioSummary.currentValue)}</p>
              </div>
              <div className="w-px h-6" style={{ backgroundColor: colors.border }} />
              <div className="text-center">
                <p className="text-[10px] uppercase tracking-wide" style={{ color: colors.secondaryText }}>P&L</p>
                <p className="text-[12px] font-bold" style={{ color: pnlColor(portfolioSummary.unrealizedPnl) }}>
                  {portfolioSummary.unrealizedPnl >= 0 ? '+' : ''}{formatCurrency(portfolioSummary.unrealizedPnl)}
                </p>
              </div>
              {dailyCharges > 0 && (
                <>
                  <div className="w-px h-6" style={{ backgroundColor: colors.border }} />
                  <div className="text-center">
                    <p className="text-[10px] uppercase tracking-wide" style={{ color: colors.secondaryText }}>Charges</p>
                    <p className="text-[12px] font-bold" style={{ color: '#ef4444' }}>-{formatCurrency(dailyCharges)}</p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Nyx portfolio summary */}
          {portfolioInsights.summary && (
            <div className="rounded-lg p-2 mb-3 border flex items-start gap-2" style={{ backgroundColor: `${colors.accent}15`, borderColor: `${colors.accent}30` }}>
              <Sparkles className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: colors.accent }} />
              <p className="text-[13px] italic" style={{ color: colors.primaryText }}>"{portfolioInsights.summary}"</p>
            </div>
          )}

          {portfolioLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-20 rounded-lg animate-pulse" style={{ backgroundColor: colors.inputBg }} />
              ))}
            </div>
          ) : portfolioError ? (
            <InlineError message={portfolioError} onRetry={fetchPortfolio} />
          ) : portfolio.length === 0 ? (
            <div className="text-center py-8">
              <Wallet className="w-10 h-10 mx-auto mb-3 opacity-30" style={{ color: colors.secondaryText }} />
              <p className="text-[14px] font-medium" style={{ color: colors.secondaryText }}>No holdings yet</p>
              <p className="text-[13px] mt-1" style={{ color: colors.secondaryText }}>Buy stocks to build your portfolio</p>
            </div>
          ) : (
            <div className="space-y-2">
              {portfolio.map((pos, i) => {
                const symbol = (pos.symbol || '').replace('.NS', '');
                const hasPriceData = pos.current_price != null;
                const pnl = hasPriceData ? (pos.unrealized_pnl ?? 0) : null;
                const pnlPct = hasPriceData ? (pos.pnl_percentage ?? 0) : null;
                const aiInsight = portfolioInsights.positions?.[pos.symbol] || '';
                const isExpanded = expandedSymbol === pos.symbol;
                // Individual trades for this symbol
                const trades = openTrades.filter(t => t.symbol === pos.symbol || t.symbol === `${symbol}.NS`);
                const earliestEntry = trades.length > 0
                  ? trades.reduce((min, t) => (t.executed_at || t.entry_date) < (min.executed_at || min.entry_date) ? t : min, trades[0])
                  : null;
                const holdDays = earliestEntry
                  ? Math.floor((Date.now() - new Date(earliestEntry.executed_at || earliestEntry.entry_date)) / 86400000)
                  : null;

                return (
                  <div key={pos.symbol || i} className="rounded-2xl overflow-hidden" style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, border: `1px solid ${isExpanded ? colors.accent + '60' : colors.accent + '25'}` }}>
                    {/* Summary row — click to expand */}
                    <button
                      className="w-full p-2 text-left"
                      onClick={() => setExpandedSymbol(isExpanded ? null : pos.symbol)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 mr-2">
                          <div className="flex items-center gap-1.5">
                            <p className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>{symbol}</p>
                            {holdDays !== null && (
                              <span className="text-[12px] px-1 py-0.5 rounded" style={{ backgroundColor: colors.inputBg, color: colors.secondaryText }}>{holdDays}d</span>
                            )}
                          </div>
                          <p className="text-[12px] mt-0.5" style={{ color: colors.secondaryText }}>
                            {pos.quantity} shares · avg {formatPrice(pos.avg_buy_price || 0)}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-right">
                            <p className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>
                              {hasPriceData ? formatCurrency(pos.current_value) : <span style={{ color: colors.secondaryText }}>—</span>}
                            </p>
                            {pnl !== null ? (
                              <p className="text-[12px] font-medium" style={{ color: pnlColor(pnl) }}>
                                {pnl >= 0 ? '+' : ''}{formatCurrency(pnl)} ({pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(1)}%)
                              </p>
                            ) : (
                              <p className="text-[12px]" style={{ color: colors.secondaryText }}>price unavailable</p>
                            )}
                          </div>
                          {isExpanded
                            ? <ChevronUp className="w-3.5 h-3.5 shrink-0" style={{ color: colors.secondaryText }} />
                            : <ChevronDown className="w-3.5 h-3.5 shrink-0" style={{ color: colors.secondaryText }} />}
                        </div>
                      </div>
                      {aiInsight && !isExpanded && (
                        <div className="mt-1.5 flex items-center gap-1.5">
                          <Sparkles className="w-2.5 h-2.5 shrink-0" style={{ color: colors.accent }} />
                          <p className="text-[13px]" style={{ color: colors.secondaryText }}>{aiInsight}</p>
                        </div>
                      )}
                    </button>

                    {/* Expanded detail */}
                    {isExpanded && (
                      <div className="px-3 pb-3 space-y-3 border-t" style={{ borderColor: colors.border }}>
                        {/* Price row — always 3 columns */}
                        <div className="flex items-center gap-1 pt-2">
                          {[
                            { label: 'Avg Buy', value: formatPrice(pos.avg_buy_price) },
                            { label: 'Current', value: pos.current_price ? formatPrice(pos.current_price) : '—' },
                            { label: 'Invested', value: formatCurrency(pos.total_invested) },
                          ].map(({ label, value }, idx, arr) => (
                            <div key={label} className="flex-1 rounded-lg px-2 py-1.5 text-center" style={{ backgroundColor: colors.inputBg }}>
                              <p className="text-[10px] uppercase tracking-wide" style={{ color: colors.secondaryText }}>{label}</p>
                              <p className="text-[12px] font-bold" style={{ color: colors.primaryText }}>{value}</p>
                            </div>
                          ))}
                        </div>

                        {/* Individual trade lots */}
                        {trades.length > 0 && (
                          <div className="space-y-1.5">
                            <p className="text-[13px] font-semibold" style={{ color: colors.secondaryText }}>
                              {trades.length} lot{trades.length > 1 ? 's' : ''}
                            </p>
                            {trades.map((t, ti) => {
                              const sl = parseFloat(t.stop_loss || 0);
                              const tp = parseFloat(t.target_price || 0);
                              return (
                                <div key={ti} className="rounded-lg p-2.5" style={{ backgroundColor: colors.inputBg }}>
                                  <div className="flex items-center justify-between mb-1.5">
                                    <div className="flex items-center gap-1.5 text-[13px]" style={{ color: colors.secondaryText }}>
                                      <Calendar className="w-3 h-3" />
                                      <span>{formatDate(t.executed_at || t.entry_date)}</span>
                                    </div>
                                    <span className="text-[13px] font-medium" style={{ color: colors.primaryText }}>
                                      {t.quantity} × {formatPrice(t.price || t.entry_price)}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-3 text-[13px]">
                                    {sl > 0 && (
                                      <div className="flex items-center gap-1">
                                        <AlertTriangle className="w-2.5 h-2.5" style={{ color: colors.loss }} />
                                        <span style={{ color: colors.loss }}>SL {formatPrice(sl)}</span>
                                      </div>
                                    )}
                                    {tp > 0 && (
                                      <div className="flex items-center gap-1">
                                        <Target className="w-2.5 h-2.5" style={{ color: colors.profit }} />
                                        <span style={{ color: colors.profit }}>TP {formatPrice(tp)}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* AI insight */}
                        {aiInsight && (
                          <div className="flex items-start gap-1.5">
                            <Sparkles className="w-2.5 h-2.5 mt-0.5 shrink-0" style={{ color: colors.accent }} />
                            <p className="text-[13px]" style={{ color: colors.secondaryText }}>{aiInsight}</p>
                          </div>
                        )}

                        {/* Close / Cover + View Chart actions */}
                        <div className="flex gap-2">
                          <button
                            onClick={() => setSellPosition({
                              symbol: pos.symbol,
                              lot_qty: pos.lot_qty || 1,
                              lot_size: pos.lot_size || 1,
                              current_price: pos.current_price || 0,
                              contract_id: pos.contract_id || null,
                              trade_type: pos.trade_type,
                            })}
                            className="flex-1 py-2 rounded-lg text-[13px] font-semibold text-center"
                            style={{ backgroundColor: '#ef444420', color: '#ef4444', border: '1px solid #ef444440' }}
                          >
                            {pos.trade_type === 'SELL' ? 'Cover (Buy)' : 'Sell'}
                          </button>
                          <button
                            onClick={() => navigate(`/stock/${symbol}`)}
                            className="flex-1 py-2 rounded-lg text-[13px] font-medium text-center"
                            style={{ backgroundColor: `${colors.accent}15`, color: colors.accent }}
                          >
                            View Chart →
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── TRADE REPLAY OVERLAY ──────────────────────────────── */}
      {replayOrder && (
        <div className="fixed inset-0 z-50 flex flex-col" style={{ backgroundColor: 'rgba(0,0,0,0.92)' }}>
          <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: '#1e293b' }}>
            <p className="text-[14px] font-semibold text-white">Trade Replay</p>
            <button onClick={() => setReplayOrder(null)}>
              <X className="w-5 h-5 text-white opacity-60" />
            </button>
          </div>
          <div className="flex-1 overflow-hidden px-3 pb-4 pt-3">
            <TradeReplay
              symbol={replayOrder.symbol}
              entryDate={replayOrder.executed_at || replayOrder.placed_at}
              exitDate={replayOrder.exit_date || null}
              entryPrice={parseFloat(replayOrder.executed_price || replayOrder.price || replayOrder.entry_price)}
              exitPrice={replayOrder.exit_price ? parseFloat(replayOrder.exit_price) : null}
              stopLoss={replayOrder.stop_loss_price != null ? parseFloat(replayOrder.stop_loss_price) : replayOrder.stop_loss != null ? parseFloat(replayOrder.stop_loss) : null}
              targetPrice={replayOrder.target_price ? parseFloat(replayOrder.target_price) : null}
              productType={replayOrder.product_type || (replayOrder.order_variant === 'INTRADAY' ? 'INTRADAY' : 'SWING')}
              side={replayOrder.trade_type || replayOrder.order_type || 'BUY'}
              height={420}
              onComplete={() => {}}
            />
          </div>
        </div>
      )}

      {/* ── MODIFY ORDER MODAL ────────────────────────────────── */}
      {modifyOrder && (
        <OrderModal
          isOpen={true}
          onClose={() => setModifyOrder(null)}
          stock={{
            symbol: modifyOrder.symbol,
            company_name: modifyOrder.symbol?.replace('.NS', ''),
            current_price: modifyOrder.limit_price ? Number(modifyOrder.limit_price) : 0,
            change_pct: 0,
          }}
          orderType={modifyOrder.order_type || modifyOrder.trade_type || 'BUY'}
          editOrder={modifyOrder}
          onOrderUpdated={(updated) => {
            setTodayOrders(prev => prev.map(o =>
              o.trade_id === modifyOrder.trade_id
                ? {
                    ...o,
                    limit_price: updated.limit_price,
                    stop_loss_price: updated.stop_loss_price,
                    target_price: updated.target_price,
                    quantity: updated.quantity,
                    notes: updated.notes,
                    strategy_tags: updated.strategy_tags,
                    price: updated.limit_price,
                  }
                : o
            ));
            setModifyOrder(null);
          }}
        />
      )}

      {/* ── SELL FROM PORTFOLIO MODAL ────────────────────────────── */}
      {sellPosition && (
        <OrderModal
          isOpen={true}
          onClose={() => setSellPosition(null)}
          stock={{
            symbol: sellPosition.symbol,
            company_name: sellPosition.symbol?.replace('.NS', ''),
            current_price: sellPosition.current_price,
            change_pct: 0,
            contract_id: sellPosition.contract_id,
            lot_size: sellPosition.lot_size || 1,
          }}
          orderType={sellPosition.trade_type === 'SELL' ? 'BUY' : 'SELL'}
          initialValues={{ quantity: sellPosition.lot_qty || 1 }}
          onOrderUpdated={() => {
            setSellPosition(null);
            fetchPortfolio();
          }}
        />
      )}

      {/* ── CANCEL CONFIRMATION MODAL ─────────────────────────── */}
      {cancelConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ backgroundColor: colors.overlayBg }}
          onClick={() => setCancelConfirm(null)}
        >
          <div
            className="app-card w-full max-w-sm rounded-lg p-5 shadow-2xl"
            style={{ background: `linear-gradient(135deg, ${colors.accent}40 0%, ${colors.accent}15 100%), ${colors.cardBg}`, border: `1px solid ${colors.accentBorder}` }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-3"
                style={{ backgroundColor: '#ef444420' }}>
                <X className="w-5 h-5" style={{ color: '#ef4444' }} />
              </div>
              <p className="text-[14px] font-semibold mb-1" style={{ color: colors.primaryText }}>
                Cancel this order?
              </p>
              <p className="text-[13px]" style={{ color: colors.secondaryText }}>
                {(cancelConfirm.trade_type || '').toUpperCase()} {cancelConfirm.quantity} ×{' '}
                {(cancelConfirm.symbol || '').replace('.NS', '')} will be removed from the queue.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                className="flex-1 py-1.5 rounded-lg text-[14px] font-semibold border"
                style={{ color: colors.secondaryText, borderColor: colors.border, backgroundColor: colors.inputBg }}
                onClick={() => setCancelConfirm(null)}
              >
                Keep Order
              </button>
              <button
                className="flex-1 py-1.5 rounded-lg text-[14px] font-semibold"
                style={{ backgroundColor: '#ef4444', color: '#fff' }}
                onClick={async () => {
                  try {
                    await axios.delete(`${API_URL}/trade/orders/${cancelConfirm.trade_id}`);
                    setTodayOrders(prev => prev.map(o =>
                      o.trade_id === cancelConfirm.trade_id ? { ...o, status: 'CANCELLED' } : o
                    ));
                    setExpandedOrder(null);
                  } catch (err) {
                    console.error('Cancel failed', err);
                    toast.error("Couldn't cancel order. Please try again.");
                  } finally {
                    setCancelConfirm(null);
                  }
                }}
              >
                Yes, Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrdersPage;
